import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isCancel = location.pathname.includes("cancel");
  const [status, setStatus] = useState(isCancel ? "cancelled" : "checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (isCancel) return;
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
      } catch {}
      if (attempts.current >= 12) {
        setStatus("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, isCancel]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#0a0a0c] px-4 text-center">
      <div data-testid="payment-result" className="max-w-sm space-y-5">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-[#ff2a5f]" />
            <p className="text-white">Confirming your payment…</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" />
            <h1 className="font-display text-2xl font-bold text-white">Payment complete</h1>
            <p className="text-sm text-zinc-400">Your media is now in your library.</p>
            <Button data-testid="go-dashboard" onClick={() => navigate("/dashboard")} className="w-full bg-[#ff2a5f] text-white hover:bg-[#e01f50]">
              Go to my library
            </Button>
          </>
        )}
        {(status === "cancelled" || status === "failed") && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-rose-500" />
            <h1 className="font-display text-2xl font-bold text-white">Payment {status}</h1>
            <p className="text-sm text-zinc-400">No charge was made. You can try again anytime.</p>
            <Button onClick={() => navigate("/")} className="w-full bg-[#ff2a5f] text-white hover:bg-[#e01f50]">Back to store</Button>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-amber-400" />
            <p className="text-white">Still processing. Check your library shortly.</p>
            <Button onClick={() => navigate("/dashboard")} className="w-full bg-[#ff2a5f] text-white hover:bg-[#e01f50]">Go to my library</Button>
          </>
        )}
        {status === "error" && <p className="text-white">Something went wrong.</p>}
      </div>
    </div>
  );
}
